import { Money } from '../../components/ui/Money';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, Wallet, Plus, Clock, CheckCircle2, XCircle, Users, Send, ChevronRight, X } from 'lucide-react';
import useBlinkHighlight from '../../hooks/useBlinkHighlight';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending_manager: { label: 'Awaiting Manager', color: 'bg-amber-100 text-amber-700', icon: Clock },
  pending_hr: { label: 'Awaiting HR', color: 'bg-sky-100 text-sky-700', icon: Clock },
  pending_finance: { label: 'Awaiting Finance', color: 'bg-indigo-100 text-indigo-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const moneyText = (n) => `AED ${Number(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money = (n) => <Money value={n} digits={2} />;

const PortalSalaryAdvance = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const emp = JSON.parse(localStorage.getItem('portal_employee') || '{}');
  const headers = { Authorization: `Bearer ${token}` };

  const [myRequests, setMyRequests] = useState([]);
  const [mgrPending, setMgrPending] = useState([]);
  const [settings, setSettings] = useState({ max_amount: 0 });
  const [advanceBalance, setAdvanceBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get('tab');
    return t === 'manager' ? 'manager' : 'my';
  })();
  const [tab, setTab] = useState(initialTab); // my | manager
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', reason: '', repayment_months: '' });
  const [submitting, setSubmitting] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [commentFor, setCommentFor] = useState(null); // { id, action }
  const [comment, setComment] = useState('');
  const requestRefs = useRef({});
  // Highlight a request row when arrived via notification deep-link.
  // Both tabs feed the same ref map — the matching id wins.
  useBlinkHighlight(requestRefs, !loading && (myRequests.length > 0 || mgrPending.length > 0));

  const isManager = ['Manager', 'Management', 'Top Management', 'Owner'].includes(emp.employee_role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [
        axios.get(`${API}/api/salary-advances/my-requests`, { headers }),
        axios.get(`${API}/api/salary-advances/settings`, { headers }),
      ];
      if (isManager) {
        reqs.push(axios.get(`${API}/api/salary-advances/manager/pending`, { headers }));
      }
      const results = await Promise.all(reqs);
      setMyRequests(results[0].data || []);
      setSettings(results[1].data || { max_amount: 0 });
      if (isManager) setMgrPending(results[2].data || []);
      axios.get(`${API}/api/salary-advances/my-advance-balance`, { headers })
        .then(r => setAdvanceBalance(r.data))
        .catch(() => {});
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    if (settings.max_amount && amount > settings.max_amount) {
      toast({ title: `Max allowed is ${moneyText(settings.max_amount)}`, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/salary-advances/my-requests`, {
        amount,
        reason: form.reason,
        repayment_months: form.repayment_months ? parseInt(form.repayment_months) : null,
      }, { headers });
      toast({ title: 'Request submitted' });
      setShowForm(false);
      setForm({ amount: '', reason: '', repayment_months: '' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try {
      await axios.delete(`${API}/api/salary-advances/my-requests/${id}`, { headers });
      toast({ title: 'Cancelled' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const mgrDecide = async (id, action) => {
    setActingId(id);
    try {
      await axios.post(`${API}/api/salary-advances/manager/${id}/action`,
        { action, comment }, { headers });
      toast({ title: action === 'approve' ? 'Approved' : 'Rejected' });
      setCommentFor(null); setComment('');
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setActingId(null); }
  };

  if (loading) return <div className="p-6 text-center text-gray-500" data-testid="salary-advance-loading">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="portal-salary-advance-page">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-600" />
            Salary Advance
          </h1>
          <p className="text-xs text-gray-500">
            {settings.max_amount
              ? `Max advance allowed: ${moneyText(settings.max_amount)}`
              : 'No company cap set — ask HR for guidance'}
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-advance-request-btn">
          <Plus className="h-4 w-4 mr-1" /> New Request
        </Button>
      </div>

      {/* My advance balance (payroll advances) */}
      {advanceBalance && advanceBalance.count > 0 && (
        <div className="grid grid-cols-3 gap-2" data-testid="portal-advance-balance">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-gray-900"><Money value={advanceBalance.total_advanced} /></p>
              <p className="text-[10px] text-gray-400">Total Advanced</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-emerald-600"><Money value={advanceBalance.total_paid} /></p>
              <p className="text-[10px] text-gray-400">Paid Off</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3 text-center">
              <p className={`text-lg font-bold ${advanceBalance.outstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}><Money value={advanceBalance.outstanding} /></p>
              <p className="text-[10px] text-gray-400">Balance Due</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      {isManager && (
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setTab('my')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${tab === 'my' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
            data-testid="tab-my-requests"
          >My Requests</button>
          <button
            onClick={() => setTab('manager')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition flex items-center gap-1 ${tab === 'manager' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
            data-testid="tab-manager-queue"
          >
            <Users className="h-4 w-4" />
            Manager Queue
            {mgrPending.length > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 rounded-full">{mgrPending.length}</span>}
          </button>
        </div>
      )}

      {/* My Requests */}
      {tab === 'my' && (
        <div className="space-y-2">
          {myRequests.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-gray-500">
              No requests yet.
            </CardContent></Card>
          ) : myRequests.map(r => {
            const meta = STATUS_META[r.status] || STATUS_META.pending_manager;
            const canCancel = r.status === 'pending_manager' || r.status === 'pending_hr';
            return (
              <Card key={r.id} ref={(el) => { requestRefs.current[r.id] = el; }} data-testid={`my-request-${r.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-lg font-bold text-gray-900">{money(r.amount)}</span>
                        <Badge className={meta.color}>{meta.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">Submitted {formatDate(r.created_at)}</p>
                    </div>
                    {canCancel && (
                      <Button variant="ghost" size="sm" onClick={() => cancel(r.id)} className="text-red-500" data-testid={`cancel-request-${r.id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {r.reason && <p className="text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2">{r.reason}</p>}
                  {r.repayment_months && <p className="text-xs text-gray-500">Requested repayment over {r.repayment_months} month(s)</p>}
                  {r.timeline && r.timeline.length > 0 && (
                    <div className="border-t pt-2 space-y-1">
                      {r.timeline.map((t, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <ChevronRight className="h-3 w-3 text-gray-400 mt-0.5" />
                          <div className="flex-1">
                            <span className="capitalize font-medium text-gray-700">{t.stage}</span>
                            <span className="text-gray-500"> — {t.action} by {t.actor}</span>
                            {t.comment && <span className="block text-gray-500 italic">"{t.comment}"</span>}
                          </div>
                          <span className="text-gray-400">{formatDate(t.at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Manager queue */}
      {tab === 'manager' && (
        <div className="space-y-2">
          {mgrPending.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-gray-500">
              No pending requests from your team.
            </CardContent></Card>
          ) : mgrPending.map(r => (
            <Card key={r.id} ref={(el) => { requestRefs.current[r.id] = el; }} data-testid={`mgr-request-${r.id}`}>
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="font-semibold text-gray-900">{r.employee_name} <span className="text-xs text-gray-400">({r.employee_code})</span></p>
                  <p className="text-xs text-gray-500">{r.department}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-bold">{money(r.amount)}</span>
                  {r.repayment_months && <Badge variant="outline">{r.repayment_months} month(s)</Badge>}
                </div>
                {r.reason && <p className="text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2">{r.reason}</p>}
                {commentFor?.id === r.id ? (
                  <div className="space-y-2">
                    <Textarea placeholder="Add a note (optional)" value={comment} onChange={e => setComment(e.target.value)} rows={2} data-testid={`comment-${r.id}`} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => mgrDecide(r.id, commentFor.action)} disabled={actingId === r.id}
                        className={commentFor.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                        data-testid={`confirm-${commentFor.action}-${r.id}`}>
                        Confirm {commentFor.action}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setCommentFor(null); setComment(''); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setCommentFor({ id: r.id, action: 'approve' })} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setCommentFor({ id: r.id, action: 'reject' })} className="text-red-600 border-red-300" data-testid={`reject-${r.id}`}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New request dialog */}
      {showForm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="new-request-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full z-10">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <Send className="h-4 w-4 text-emerald-600" /> New Salary Advance Request
              </h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={submit} className="p-4 space-y-3">
              <div>
                <Label>Amount (AED)</Label>
                <Input type="number" min="1" step="0.01" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  required data-testid="form-amount" />
                {settings.max_amount > 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">Max: {money(settings.max_amount)}</p>
                )}
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea rows={3} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} data-testid="form-reason" />
              </div>
              <div>
                <Label>Preferred repayment months (optional)</Label>
                <Input type="number" min="1" max="24" value={form.repayment_months}
                  onChange={e => setForm(f => ({ ...f, repayment_months: e.target.value }))}
                  placeholder="e.g. 3" data-testid="form-months" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="form-submit">
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalSalaryAdvance;
